import { useEffect, useMemo, useState } from "react";
import { worksList, workTypes, workYears } from "@/mocks/works";
import { useSeo } from "@/hooks/useSeo";
import Breadcrumb from "@/components/base/Breadcrumb";
import WorkCard from "@/components/feature/WorkCard";

const INITIAL_COUNT = 6;
const STEP = 3;

export default function WorksPage() {
  useSeo({
    title: "作品介绍 - 风间由美",
    description:
      "风间由美作品介绍栏目，收录电影、电视剧、音乐与纪录片作品档案，可按作品类型与发行年份多选筛选，查看封面、简介与演职人员信息。",
    keywords: "风间由美作品,风间由美电影,风间由美电视剧,风间由美音乐,作品档案",
  });

  const [types, setTypes] = useState<string[]>([]);
  const [years, setYears] = useState<number[]>([]);
  const [visible, setVisible] = useState(INITIAL_COUNT);

  useEffect(() => {
    setVisible(INITIAL_COUNT);
  }, [types, years]);

  const filtered = useMemo(
    () =>
      worksList
        .filter((w) => (types.length === 0 ? true : types.includes(w.type)))
        .filter((w) => (years.length === 0 ? true : years.includes(w.year)))
        .sort((a, b) => b.year - a.year),
    [types, years],
  );

  const toggleType = (type: string) =>
    setTypes((prev) => (prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]));

  const toggleYear = (year: number) =>
    setYears((prev) => (prev.includes(year) ? prev.filter((y) => y !== year) : [...prev, year]));

  const reset = () => {
    setTypes([]);
    setYears([]);
  };

  const shown = filtered.slice(0, visible);
  const hasMore = visible < filtered.length;

  return (
    <div className="mx-auto w-full max-w-[1320px] px-4 py-10 md:px-6 md:py-14">
      <Breadcrumb items={[{ label: "首页", path: "/" }, { label: "作品介绍" }]} />

      <header className="mt-6 border-b border-background-200 pb-8">
        <h1 className="font-heading text-3xl font-bold tracking-tight text-foreground-950 md:text-4xl">
          作品介绍
        </h1>
        <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
          风间由美的作品横跨电影、电视剧、音乐与纪录片。这里按类型与发行年份整理了完整的作品档案，包含封面、简介与演职人员信息，方便你快速浏览与深入了解。
        </p>
      </header>

      <div className="mt-8 grid gap-8 lg:grid-cols-12 lg:gap-10">
        <aside className="lg:col-span-3">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <div className="flex items-center justify-between">
              <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <i className="ri-filter-3-line text-primary-500" />
                筛选条件
              </h2>
              <button
                type="button"
                onClick={reset}
                className="cursor-pointer whitespace-nowrap text-xs font-medium text-primary-600 hover:text-primary-700"
              >
                重置
              </button>
            </div>

            <div className="mt-5">
              <h3 className="text-xs font-semibold tracking-wide text-foreground-500 uppercase">
                作品类型
              </h3>
              <div className="mt-3 flex flex-col gap-2.5">
                {workTypes.map((type) => (
                  <label
                    key={type}
                    className="flex cursor-pointer items-center gap-3 text-sm text-foreground-800"
                  >
                    <input
                      type="checkbox"
                      checked={types.includes(type)}
                      onChange={() => toggleType(type)}
                      className="h-4 w-4 cursor-pointer accent-primary-500"
                    />
                    {type}
                    <span className="ml-auto text-xs text-foreground-400">
                      {worksList.filter((w) => w.type === type).length}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div className="mt-6 border-t border-background-200 pt-5">
              <h3 className="text-xs font-semibold tracking-wide text-foreground-500 uppercase">
                发行年份
              </h3>
              <div className="mt-3 flex flex-wrap gap-2">
                {workYears.map((year) => (
                  <button
                    key={year}
                    type="button"
                    onClick={() => toggleYear(year)}
                    className={`cursor-pointer whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors ${
                      years.includes(year)
                        ? "bg-accent-500 text-secondary-950"
                        : "bg-background-100 text-foreground-700 hover:bg-background-200"
                    }`}
                  >
                    {year}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </aside>

        <div className="lg:col-span-9">
          <div className="flex items-center justify-between">
            <p className="text-sm text-foreground-500">
              共 <strong className="text-primary-600">{filtered.length}</strong> 部作品
            </p>
            {(types.length > 0 || years.length > 0) && (
              <div className="flex flex-wrap items-center gap-2">
                {[...types, ...years.map(String)].map((tag) => (
                  <span
                    key={tag}
                    className="rounded-full bg-secondary-100 px-2.5 py-1 text-xs text-secondary-900"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>

          {shown.length > 0 ? (
            <>
              <div className="mt-5 grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3">
                {shown.map((work) => (
                  <WorkCard key={work.id} item={work} />
                ))}
              </div>
              {hasMore ? (
                <div className="mt-9 flex justify-center">
                  <button
                    type="button"
                    onClick={() => setVisible((v) => v + STEP)}
                    className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-primary-300 bg-background-50 px-6 py-3 text-sm font-semibold text-primary-600 transition-colors hover:bg-primary-50"
                  >
                    <i className="ri-add-line" />
                    加载更多作品
                  </button>
                </div>
              ) : (
                <p className="mt-9 text-center text-xs text-foreground-400">已经到底啦，感谢浏览</p>
              )}
            </>
          ) : (
            <div className="mt-6 flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-100/60 px-6 py-16 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-50 text-primary-500">
                <i className="ri-inbox-line text-2xl" />
              </span>
              <h3 className="mt-4 font-heading text-lg font-semibold text-foreground-900">
                没有匹配的作品
              </h3>
              <p className="mt-2 max-w-md text-sm text-foreground-600">
                当前筛选条件下暂无作品，试试调整类型或年份，或直接重置筛选。
              </p>
              <button
                type="button"
                onClick={reset}
                className="mt-6 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-refresh-line" />
                重置筛选
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}